import React from "react";
import { Card, Space, Typography, Row, Col, Divider, Switch } from "antd";
import {
  EnvironmentOutlined,
  TagOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { LocationType } from "../../../components/schedule/modal/location-types";

const { Text } = Typography;

interface LocationColorLegendProps {
  expanded?: boolean;
  onToggleExpanded?: (expanded: boolean) => void;
  locations?: LocationType[];
}

const LocationColorLegend: React.FC<LocationColorLegendProps> = ({
  expanded = true,
  onToggleExpanded,
  locations,
}) => {
  // Your custom category colors
  const categoryColors = [
    { key: "scheduled", label: "Scheduled Work", color: "#52c41a" },
    { key: "timeoffpending", label: "Time Off Pending", color: "#fa8c16" },
    { key: "timeoff", label: "Time Off Approved", color: "#ff4d4f" },
  ];

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      bodyStyle={{
        padding: expanded ? undefined : 0,
        height: expanded ? undefined : 0,
        overflow: "hidden",
      }}
      title={
        <Space>
          <EnvironmentOutlined style={{ color: "#1890ff" }} />
          <Text strong>Color Legend</Text>
        </Space>
      }
      extra={
        <Space align="center" size={8}>
          <Text style={{ fontSize: 13, color: "#8c8c8c" }}>
            {expanded ? "Hide" : "Show"}
          </Text>
          <Switch
            checked={expanded}
            onChange={(checked) => onToggleExpanded?.(checked)}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
            size="small"
          />
        </Space>
      }
    >
      {expanded && (
        <>
          <Row gutter={[24, 16]}>
            {/* Locations */}
            <Col xs={24} md={12}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <EnvironmentOutlined
                    style={{ color: "#1890ff", fontSize: 16 }}
                  />
                  <Text strong style={{ fontSize: 14 }}>
                    Locations (Card Background)
                  </Text>
                </div>

                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {locations?.map((location) => (
                    <div
                      key={location.key}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          backgroundColor: location.color,
                          border: "1px solid #d9d9d9",
                          flexShrink: 0,
                        }}
                      />
                      <Text style={{ fontSize: 13 }}>{location.name}</Text>
                    </div>
                  ))}
                </Space>
              </Space>
            </Col>

            {/* Categories */}
            <Col xs={24} md={12}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TagOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                  <Text strong style={{ fontSize: 14 }}>
                    Categories (Tags)
                  </Text>
                </div>

                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {categoryColors.map((category) => (
                    <div
                      key={category.key}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          backgroundColor: category.color,
                          border: "1px solid #d9d9d9",
                          flexShrink: 0,
                        }}
                      />
                      <Text style={{ fontSize: 13 }}>{category.label}</Text>
                    </div>
                  ))}
                </Space>
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0 12px 0" }} />

          {/* Color Usage Explanation */}
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong style={{ fontSize: 13, color: "#595959" }}>
              How Colors Work:
            </Text>
            <Space direction="vertical" size={4}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 20,
                    height: 12,
                    backgroundColor: "#1890ff",
                    borderRadius: 4,
                  }}
                />
                <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                  <strong>Card Background</strong> = Employee's Location Color
                </Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    backgroundColor: "#52c41a",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 500,
                  }}
                >
                  Tag
                </div>
                <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                  <strong>Category Tags</strong> = Schedule Type (scheduled,
                  timeoff, etc.)
                </Text>
              </div>
            </Space>
          </Space>
        </>
      )}
    </Card>
  );
};

export default LocationColorLegend;
